import React, { useState, useEffect } from 'react';
import api from '../http';
import CandidateRegistrationForm from './CandidateRegistrationForm';
import SigningQRDisplay from './SigningQRDisplay';
import './RegistrationApp.css';

/**
 * Staff-operated candidate registration station.
 * Route: /register
 *
 * Requires admin session. Staff selects the active event, then registers
 * candidates by scanning their driver's license or typing names manually.
 * Candidates scan the event QR code to sign required documents on their phone.
 */
export default function RegistrationApp() {
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [stats, setStats] = useState({ total: 0, signed: 0 });
  const [signCode, setSignCode] = useState('');
  const [signUrl, setSignUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      await api.get('/api/auth/me');
      setAuthed(true);
      loadEvents();
    } catch {
      setAuthed(false);
    }
  };

  const loadEvents = async () => {
    try {
      const res = await api.get('/api/registration/events');
      setEvents(res.data);
      if (res.data.length === 1) {
        selectEvent(res.data[0]);
      }
    } catch (e) {
      setError('Failed to load events');
    }
  };

  const loadStats = async (eventId) => {
    try {
      const res = await api.get(`/api/registration/events/${eventId}/candidates`);
      const candidates = res.data;
      setStats({
        total: candidates.length,
        signed: candidates.filter((c) => c.documents_signed_at).length
      });
    } catch (_) {}
  };

  const loadSignCode = async (eventId) => {
    try {
      const res = await api.get(`/api/registration/events/${eventId}/sign-code`);
      setSignCode(res.data.sign_code);
      setSignUrl(res.data.sign_url);
    } catch (e) {
      console.error('Failed to load sign code', e);
    }
  };

  const selectEvent = (ev) => {
    setSelectedEvent(ev);
    loadStats(ev.id);
    loadSignCode(ev.id);
  };

  const handleCandidateAdded = () => {
    if (selectedEvent) loadStats(selectedEvent.id);
  };

  useEffect(() => {
    if (selectedEvent) {
      loadStats(selectedEvent.id);
      loadSignCode(selectedEvent.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.id]);

  // ── Loading / auth ────────────────────────────────────────────────────
  if (authed === null) {
    return (
      <div className="reg-app">
        <div className="reg-loading">Loading…</div>
      </div>
    );
  }

  if (authed === false) {
    return (
      <div className="reg-app">
        <div className="reg-auth-error">
          <h2>Admin login required</h2>
          <p>Please log in as an admin to access the registration station.</p>
          <a className="btn btn-primary" href="/admin/login">
            Log In
          </a>
        </div>
      </div>
    );
  }

  // ── Event selection ───────────────────────────────────────────────────
  if (!selectedEvent) {
    return (
      <div className="reg-app">
        <header className="reg-header">
          <div className="reg-logo">
            <svg viewBox="0 0 32 40" fill="none" className="reg-flame" aria-hidden="true">
              <path d="M16 2C16 2 8 10 8 18C8 22.4 10.4 26.2 14 28.4C13.2 26.8 13 25 13.4 23.2C14.6 25 16.4 26.2 18 26C17 24 17.2 21.6 18.8 20C19.6 22 20.8 23.4 22 24C22 20 20 16.4 16 14C17.6 16.4 18 19.2 16.8 22C15.2 20 14.4 17.2 15.2 14.4C13.2 16.4 12 19.4 12.4 22.6C10.4 20.4 9.6 17.2 10.4 14C9.2 16 8.4 18.4 8.8 20.8C8.2 19.8 8 18.6 8 17.4C8 23.4 11.6 28.4 17 30C17.4 30.2 17.8 30.2 18.2 30.4C22.6 28.6 26 24.4 26 18C26 10 18 2 16 2Z" fill="#f97316"/>
            </svg>
            <span className="reg-logo-text">EVOLUTION</span>
          </div>
          <h1 className="reg-title">Candidate Registration</h1>
        </header>

        <div className="reg-content">
          <h2 className="section-title">Select Event</h2>
          {error && <p className="form-error">{error}</p>}
          {events.length === 0 ? (
            <p className="reg-empty">No active events found. Please create and activate an event in Admin Setup first.</p>
          ) : (
            <div className="event-list">
              {events.map((ev) => (
                <button
                  key={ev.id}
                  className="event-card"
                  onClick={() => selectEvent(ev)}
                >
                  <span className="event-card-name">{ev.name}</span>
                  {ev.event_date && (
                    <span className="event-card-date">
                      {new Date(ev.event_date).toLocaleDateString()}
                    </span>
                  )}
                  {ev.location && (
                    <span className="event-card-location">{ev.location}</span>
                  )}
                  <span className={`event-badge event-badge-${ev.status}`}>{ev.status}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Main registration interface ───────────────────────────────────────
  return (
    <div className="reg-app">
      <header className="reg-header">
        <div className="reg-header-left">
          <svg viewBox="0 0 32 40" fill="none" className="reg-flame" aria-hidden="true">
            <path d="M16 2C16 2 8 10 8 18C8 22.4 10.4 26.2 14 28.4C13.2 26.8 13 25 13.4 23.2C14.6 25 16.4 26.2 18 26C17 24 17.2 21.6 18.8 20C19.6 22 20.8 23.4 22 24C22 20 20 16.4 16 14C17.6 16.4 18 19.2 16.8 22C15.2 20 14.4 17.2 15.2 14.4C13.2 16.4 12 19.4 12.4 22.6C10.4 20.4 9.6 17.2 10.4 14C9.2 16 8.4 18.4 8.8 20.8C8.2 19.8 8 18.6 8 17.4C8 23.4 11.6 28.4 17 30C17.4 30.2 17.8 30.2 18.2 30.4C22.6 28.6 26 24.4 26 18C26 10 18 2 16 2Z" fill="#f97316"/>
          </svg>
          <div>
            <div className="reg-logo-text">EVOLUTION</div>
            <div className="reg-event-name">{selectedEvent.name}</div>
          </div>
        </div>
        <div className="reg-stats">
          <span className="stat-item">
            <span className="stat-num">{stats.total}</span>
            <span className="stat-label">Registered</span>
          </span>
          <span className="stat-item">
            <span className="stat-num">{stats.signed}</span>
            <span className="stat-label">Signed</span>
          </span>
        </div>
        <button className="btn-link reg-change-event" onClick={() => setSelectedEvent(null)}>
          Change Event
        </button>
      </header>

      <div className="reg-content">
        {signUrl && (
          <SigningQRDisplay
            eventId={selectedEvent.id}
            signUrl={signUrl}
            signCode={signCode}
          />
        )}
        <CandidateRegistrationForm
          event={selectedEvent}
          onCandidateAdded={handleCandidateAdded}
        />
      </div>
    </div>
  );
}
